// src/ui/CurrentPartPanel.tsx

import React from 'react';
import type { Placement, PartGroup } from '../types';

interface CurrentPartPanelProps {
  placement: Placement | null;
  group: PartGroup | null;
  onMarkPlaced: () => void;
  onSkip: () => void;
  onBack: () => void;
}

export const CurrentPartPanel: React.FC<CurrentPartPanelProps> = ({
  placement,
  group,
  onMarkPlaced,
  onSkip,
  onBack
}) => {
  if (!placement) {
    return (
      <div className="current-part-panel empty">
        <p>No part selected</p>
        <p>Select a placement from the board or group list</p>
      </div>
    );
  }
  
  const progress = group?.count && group.count > 0 
    ? Math.round(((group.placedCount || 0) / group.count) * 100)
    : 0;
  
  return (
    <div className="current-part-panel">
      <div className="part-header">
        <h3>{placement.refDes}</h3>
        <span className={`status-badge ${placement.status}`}>
          {placement.status}
        </span>
      </div>
      
      {group && (
        <div className="group-info">
          <div className="group-progress">
            <span>Group Progress: {group.placedCount || 0}/{group.count}</span>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}
      
      <div className="part-details">
        {placement.mpn && (
          <div className="detail-row">
            <span className="label">MPN:</span>
            <span className="value">{placement.mpn}</span>
          </div>
        )}
        
        {placement.manufacturer && (
          <div className="detail-row">
            <span className="label">Manufacturer:</span>
            <span className="value">{placement.manufacturer}</span>
          </div>
        )}
        
        {placement.value && (
          <div className="detail-row">
            <span className="label">Value:</span>
            <span className="value">{placement.value}</span>
          </div>
        )}
        
        {placement.footprint && (
          <div className="detail-row">
            <span className="label">Footprint:</span>
            <span className="value">{placement.footprint}</span>
          </div>
        )}
        
        <div className="detail-row">
          <span className="label">Position:</span>
          <span className="value">
            X: {placement.x.toFixed(2)}, Y: {placement.y.toFixed(2)}
          </span>
        </div>
        
        <div className="detail-row">
          <span className="label">Rotation:</span>
          <span className="value">{placement.rotation}°</span>
        </div>
        
        <div className="detail-row">
          <span className="label">Side:</span>
          <span className="value">{placement.side}</span>
        </div>
      </div>
      
      {placement.description && (
        <div className="description">
          <h4>Description</h4>
          <p>{placement.description}</p>
        </div>
      )}
      
      <div className="actions">
        <button 
          className="btn-primary"
          onClick={onMarkPlaced}
          disabled={placement.status === 'placed'}
        >
          {placement.status === 'placed' ? 'Placed' : 'Mark Placed'}
        </button>
        
        <button 
          className="btn-secondary"
          onClick={onSkip}
          disabled={placement.status === 'skipped'}
        >
          {placement.status === 'skipped' ? 'Skipped' : 'Skip'}
        </button>
        
        <button 
          className="btn-secondary"
          onClick={onBack}
        >
          Back
        </button>
      </div>
    </div>
  );
};
